import React from "react";
import { Link } from "react-router-dom";
import { useLanguage, useTheme } from "../App";
import { Sun, Moon, Globe, ArrowLeft } from "lucide-react";

const TermsConditions = () => {
  const { language, changeLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_saloneos-booking/artifacts/eysotq4t_smartbooker%20logo.png" 
                alt="SmartBooker" 
                className="h-8 w-auto"
              />
            </Link>

            <div className="flex items-center gap-4">
              <button
                onClick={() => changeLanguage(language === "it" ? "en" : "it")}
                className="p-2 rounded-full hover:bg-muted transition-colors"
              >
                <Globe className="w-5 h-5" />
              </button>
              
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full hover:bg-muted transition-colors"
              >
                {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === "it" ? "Torna alla home" : "Back to home"}
          </Link>

          <div className="prose prose-slate dark:prose-invert max-w-none">
            <h1 className="text-3xl font-serif font-medium mb-2">Termini e Condizioni – SmartBooker</h1>
            <p className="text-muted-foreground mb-8">Ultimo aggiornamento: 13/03/2026</p>

            <p>
              I presenti Termini e Condizioni disciplinano l'accesso e l'utilizzo della piattaforma SmartBooker, 
              un servizio software che consente a parrucchieri, barber shop, centri estetici e spa di gestire 
              prenotazioni online e appuntamenti con i propri clienti.
            </p>
            
            <p className="font-medium">
              Utilizzando la piattaforma, l'utente accetta integralmente i presenti Termini.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">1. Titolare del servizio</h2>
            <p>Il servizio SmartBooker è fornito da:</p>
            <div className="bg-muted/50 p-4 rounded-lg my-4">
              <p className="mb-1"><strong>AleNic Service & Consulting di Antonio Luca</strong></p>
              <p className="mb-1">Sede legale: Via Gaetano Martino 12/4 - 95036 Randazzo (CT)</p>
              <p className="mb-1">Partita IVA: 06022550872</p>
              <p>Email: <a href="mailto:info@smartbooker.it" className="text-accent">info@smartbooker.it</a></p>
            </div>

            <h2 className="text-xl font-semibold mt-8 mb-4">2. Descrizione del servizio</h2>
            <p>SmartBooker è una piattaforma SaaS (Software as a Service) che consente ai saloni di:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>gestire appuntamenti e calendario</li>
              <li>ricevere prenotazioni online dai clienti</li>
              <li>inviare notifiche e promemoria</li>
              <li>gestire operatori e servizi</li>
              <li>consultare statistiche e dati di utilizzo</li>
            </ul>
            <p className="mt-4">Il servizio è accessibile tramite web browser e dispositivi mobili.</p>

            <h2 className="text-xl font-semibold mt-8 mb-4">3. Creazione dell'account</h2>
            <p>Per utilizzare la piattaforma è necessario creare un account.</p>
            <p className="mt-4">L'utente si impegna a:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>fornire informazioni accurate e aggiornate</li>
              <li>mantenere riservate le credenziali di accesso</li>
              <li>notificare immediatamente eventuali accessi non autorizzati</li>
            </ul>
            <p className="mt-4">L'utente è responsabile di tutte le attività effettuate tramite il proprio account.</p>

            <h2 className="text-xl font-semibold mt-8 mb-4">4. Piano di abbonamento</h2>
            <p>L'accesso alla piattaforma SmartBooker avviene tramite abbonamento.</p>
            <p className="mt-4">Il piano attualmente disponibile è:</p>
            <div className="bg-accent/10 border border-accent/20 p-6 rounded-lg my-4">
              <h3 className="text-lg font-semibold mb-4">Piano unico SmartBooker</h3>
              <div className="space-y-2">
                <p className="text-2xl font-bold">€34,95 <span className="text-base font-normal text-muted-foreground">al mese</span></p>
                <p className="text-muted-foreground">oppure</p>
                <p className="text-2xl font-bold">€349 <span className="text-base font-normal text-muted-foreground">all'anno</span> <span className="text-sm text-emerald-600">(2 mesi gratuiti)</span></p>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">Il piano include tutte le funzionalità della piattaforma.</p>
            </div>

            <h2 className="text-xl font-semibold mt-8 mb-4">5. Pagamenti</h2>
            <p>I pagamenti possono essere gestiti tramite provider di pagamento esterni integrati nella piattaforma.</p>
            <p className="mt-4">
              L'utente autorizza SmartBooker ad addebitare automaticamente il costo dell'abbonamento 
              secondo la periodicità scelta.
            </p>
            <p className="mt-4">In caso di mancato pagamento, l'accesso alla piattaforma può essere sospeso.</p>

            <h2 className="text-xl font-semibold mt-8 mb-4">6. Cancellazione dell'abbonamento</h2>
            <p>L'utente può cancellare l'abbonamento in qualsiasi momento tramite le impostazioni del proprio account.</p>
            <p className="mt-4">La cancellazione avrà effetto al termine del periodo di fatturazione già pagato.</p>
            <p className="mt-4">Non sono previsti rimborsi per periodi già pagati salvo obblighi di legge.</p>

            <h2 className="text-xl font-semibold mt-8 mb-4">7. Utilizzo consentito della piattaforma</h2>
            <p>L'utente si impegna a utilizzare la piattaforma nel rispetto della normativa vigente.</p>
            <p className="mt-4">È vietato utilizzare SmartBooker per:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>attività illegali</li>
              <li>violazione dei diritti di terzi</li>
              <li>invio di comunicazioni non autorizzate o spam</li>
              <li>tentativi di accesso non autorizzato ai sistemi</li>
            </ul>
            <p className="mt-4">SmartBooker si riserva il diritto di sospendere o chiudere account che violano tali regole.</p>

            <h2 className="text-xl font-semibold mt-8 mb-4">8. Responsabilità dei saloni</h2>
            <p>I saloni che utilizzano SmartBooker sono responsabili:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>delle informazioni pubblicate sulla propria pagina di prenotazione</li>
              <li>dei servizi offerti ai clienti</li>
              <li>della gestione degli appuntamenti</li>
            </ul>
            <p className="mt-4">SmartBooker non è responsabile per eventuali controversie tra salone e cliente.</p>

            <h2 className="text-xl font-semibold mt-8 mb-4">9. Limitazione di responsabilità</h2>
            <p>SmartBooker si impegna a fornire il servizio con la massima diligenza.</p>
            <p className="mt-4">Tuttavia non garantisce che il servizio sia sempre privo di interruzioni o errori.</p>
            <p className="mt-4">SmartBooker non è responsabile per:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>perdite economiche derivanti da appuntamenti mancati</li>
              <li>errori inseriti dagli utenti</li>
              <li>interruzioni causate da servizi di terze parti</li>
              <li>problemi tecnici indipendenti dal proprio controllo</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">10. Proprietà intellettuale</h2>
            <p>
              Tutti i diritti relativi alla piattaforma SmartBooker, inclusi software, design, loghi e marchi, 
              sono di proprietà esclusiva del titolare del servizio.
            </p>
            <p className="mt-4">È vietata qualsiasi riproduzione o utilizzo non autorizzato.</p>

            <h2 className="text-xl font-semibold mt-8 mb-4">11. Protezione dei dati personali</h2>
            <p>
              Il trattamento dei dati personali è disciplinato dalla{" "}
              <Link to="/privacy-policy" className="text-accent hover:underline">Privacy Policy</Link> della piattaforma.
            </p>
            <p className="mt-4">Nel contesto della gestione delle prenotazioni:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>il salone è titolare del trattamento dei dati dei propri clienti</li>
              <li>SmartBooker agisce come responsabile del trattamento, fornendo la piattaforma tecnologica</li>
            </ul>
            <p className="mt-4">Il trattamento avviene nel rispetto del Regolamento generale sulla protezione dei dati (GDPR).</p>

            <h2 className="text-xl font-semibold mt-8 mb-4">12. Modifiche al servizio</h2>
            <p>
              SmartBooker si riserva il diritto di modificare, aggiornare o migliorare la piattaforma 
              in qualsiasi momento al fine di migliorarne le funzionalità e la sicurezza.
            </p>
            <p className="mt-4">Eventuali modifiche sostanziali saranno comunicate agli utenti.</p>

            <h2 className="text-xl font-semibold mt-8 mb-4">13. Modifiche ai Termini e Condizioni</h2>
            <p>SmartBooker può aggiornare i presenti Termini e Condizioni in qualsiasi momento.</p>
            <p className="mt-4">
              Le modifiche saranno pubblicate sulla piattaforma con indicazione della data di aggiornamento.
            </p>
            <p className="mt-4">L'utilizzo continuato del servizio costituisce accettazione delle modifiche.</p>

            <h2 className="text-xl font-semibold mt-8 mb-4">14. Legge applicabile</h2>
            <p>I presenti Termini e Condizioni sono regolati dalla legge italiana.</p>
            <p className="mt-4">
              Per qualsiasi controversia sarà competente il foro del luogo in cui ha sede il titolare del servizio, 
              salvo diverse disposizioni di legge.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">15. Contatti</h2>
            <p>Per qualsiasi informazione relativa ai presenti Termini e Condizioni è possibile contattare:</p>
            <p className="mt-2">
              <strong>Email:</strong>{" "}
              <a href="mailto:info@smartbooker.it" className="text-accent">info@smartbooker.it</a>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 border-t">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm text-muted-foreground">
            © 2026 SmartBooker - Powered by AleNic Service and Consulting
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsConditions;
